import { TrendingUp, TrendingDown, Minus, ArrowUpRight, Sparkles } from "lucide-react";

/**
 * Carte horizontale d'une catégorie thérapeutique pour le forecast 7 jours.
 *
 * Composant du sous-bloc B2 (projection 7 jours par catégorie).
 *
 * Affiche : libellé Playfair, niveau projeté + barre, tendance 7j, recommandation
 * vouvoiement D+ (fournie par le backend), et drivers déclencheurs discrets.
 */

const LEVEL_LABELS = {
  low: "Faible",
  moderate: "Normale",
  high: "Soutenue",
  very_high: "Très forte",
};

const LEVEL_SEGMENTS = { low: 1, moderate: 2, high: 3, very_high: 4 };

const TREND_META = {
  declining: { Icon: TrendingDown, label: "En baisse", color: "text-cabinet-forest" },
  stable: { Icon: Minus, label: "Stable", color: "text-cabinet-mute-1" },
  rising: { Icon: TrendingUp, label: "En hausse", color: "text-cabinet-bordeaux" },
  sharp_rise: { Icon: ArrowUpRight, label: "Forte hausse", color: "text-cabinet-bordeaux" },
};

const SOURCE_LABELS = {
  sentinelles: "Sentinelles",
  spf: "SPF",
  meteo_france: "Météo France",
};


export function ForecastCategoryCard({ category }) {
  const level = category.current_demand_level || "low";
  const trend = category.projected_trend_7d || "stable";
  const trendMeta = TREND_META[trend] || TREND_META.stable;
  const TrendIcon = trendMeta.Icon;
  const activeSegments = LEVEL_SEGMENTS[level] || 0;
  const isPriority = level === "very_high" || trend === "sharp_rise";

  // On dédoublonne et on limite à 3 drivers max pour rester lisible
  const drivers = (category.drivers || []).slice(0, 3);

  return (
    <article
      className={`bg-white border rounded-lg overflow-hidden transition-colors ${
        isPriority
          ? "border-cabinet-bordeaux/40"
          : "border-cabinet-mute-2/40 hover:border-cabinet-bordeaux/25"
      }`}
      data-testid={`forecast-card-${category.name}`}
    >
      <div className="px-6 py-5">
        {/* En-tête : libellé + tendance */}
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-playfair text-lg text-cabinet-ink font-medium tracking-tight leading-snug">
              {category.label}
            </h3>
            <p
              className="mt-1 text-[12px] tracking-wide text-cabinet-mute-1"
              data-testid="forecast-card-level"
            >
              Demande {LEVEL_LABELS[level]?.toLowerCase() || level} attendue
            </p>
          </div>
          <div
            className={`flex-shrink-0 inline-flex items-center gap-1 text-[12px] font-medium ${trendMeta.color}`}
            data-testid="forecast-card-trend"
          >
            <TrendIcon size={14} strokeWidth={2} />
            {trendMeta.label}
          </div>
        </div>

        {/* Barre niveau 4 segments */}
        <div
          className="flex items-center gap-1.5 mb-4"
          role="meter"
          aria-valuemin="0"
          aria-valuemax="4"
          aria-valuenow={activeSegments}
          aria-label={`Niveau de demande projetée ${LEVEL_LABELS[level]}`}
        >
          {[1, 2, 3, 4].map((seg) => {
            const isActive = seg <= activeSegments;
            const isVeryHigh = activeSegments === 4 && isActive;
            return (
              <span
                key={seg}
                className={`h-1.5 flex-1 rounded-sm transition-colors ${
                  isActive
                    ? isVeryHigh
                      ? "bg-cabinet-bordeaux"
                      : "bg-cabinet-bordeaux/70"
                    : "bg-cabinet-mute-2/35"
                }`}
              />
            );
          })}
        </div>

        {/* Recommandation backend (vouvoiement D+) */}
        {category.recommendation && (
          <div
            className="flex items-start gap-2.5 mt-2"
            data-testid="forecast-card-recommendation"
          >
            <Sparkles
              size={13}
              className="flex-shrink-0 mt-1 text-cabinet-gold"
              strokeWidth={1.8}
            />
            <p className="text-[13.5px] leading-relaxed text-cabinet-ink">
              {category.recommendation}
            </p>
          </div>
        )}

        {/* Drivers déclencheurs (discrets) */}
        {drivers.length > 0 && (
          <details
            className="mt-4 group"
            data-testid="forecast-card-drivers-toggle"
          >
            <summary className="cursor-pointer list-none text-[11.5px] uppercase tracking-[0.18em] font-semibold text-cabinet-mute-1 hover:text-cabinet-bordeaux transition-colors inline-flex items-center gap-1.5">
              <span>Voir les signaux déclencheurs</span>
              <span className="text-cabinet-mute-2 group-open:rotate-180 transition-transform">
                ▾
              </span>
            </summary>
            <ul
              className="mt-3 pl-4 border-l-2 border-cabinet-cream-warm space-y-1.5"
              data-testid="forecast-card-drivers-list"
            >
              {drivers.map((d, i) => (
                <li
                  key={i}
                  className="text-[12.5px] leading-relaxed text-cabinet-navy"
                >
                  <span className="text-cabinet-mute-1 font-medium tracking-wider text-[10.5px] uppercase mr-1.5">
                    {SOURCE_LABELS[d.source] || d.source} :
                  </span>
                  {d.signal}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </article>
  );
}
